import React, { useCallback, useEffect, useState } from "react";
import { Download, FileSpreadsheet, FileText } from "lucide-react";
import { api, telecharger } from "./api";
import { Modale, Eyebrow, BoutonSombre, Case, ChampTexte, ChampChoix } from "./ui";

const ELARGIR = [
  ["essais", "les élèves en cours d'essai"],
  ["stages", "les participants aux stages"],
  ["auditions", "les participants aux auditions"],
  ["spectacles", "les réservations de spectacle"],
  ["anciens", "les anciens membres"],
  ["archives", "les fiches archivées (doublons)"],
];

const JEUX = [
  ["presence", "Liste de présence", "nom, cours, horaire, professeur"],
  ["contacts", "Contacts", "nom, responsable, e-mail, téléphone, adresse"],
  ["comptabilite", "Comptabilité", "nom, statut, n° AVS, facturé, payé, solde"],
  ["tout", "Tout", "toutes les colonnes disponibles"],
];

/* Export de la liste des élèves. Par défaut les élèves actifs avec un cours semestriel ;
   l'école élargit et filtre, voit combien de lignes partiront, puis télécharge. */
export default function ExportEleves({ onFermer }) {
  const [options, setOptions] = useState({});
  const [filtres, setFiltres] = useState({ semestre: "", cours: "", discipline: "", jour: "", professeur: "", age_max: "" });
  const [jeu, setJeu] = useState("tout");
  const [apercu, setApercu] = useState(null);

  const parametres = useCallback(() => {
    const p = new URLSearchParams();
    Object.entries(options).forEach(([k, v]) => v && p.set(k, "true"));
    Object.entries(filtres).forEach(([k, v]) => v && p.set(k, v));
    return p;
  }, [options, filtres]);

  useEffect(() => {
    api(`/export/eleves/apercu?${parametres()}`).then(setApercu).catch(() => {});
  }, [parametres]);

  const sortir = (format) => {
    const p = parametres();
    p.set("format", format);
    p.set("colonnes", jeu);
    telecharger(`/export/eleves?${p}`, `1000ARTS_eleves.${format}`);
  };

  return (
    <Modale titre="Exporter la liste des élèves" onFermer={onFermer} testid="modale-export-eleves" large>
      <div className="space-y-5">
        <div className="rounded-2xl px-4 py-3 text-sm" style={{ background: "var(--primary-tint)" }}
          data-testid="export-apercu">
          <strong>{apercu ? apercu.nb : "…"} élève(s)</strong> dans cet export
          {apercu && Object.keys(apercu.par_categorie).length > 0 && (
            <span className="text-[var(--muted)]">
              {" — "}{Object.entries(apercu.par_categorie).map(([k, v]) => `${v} ${k.toLowerCase()}`).join(" · ")}
            </span>
          )}
          <div className="text-xs text-[var(--muted)] mt-1">
            Par défaut : les élèves actifs qui suivent un cours semestriel.
          </div>
        </div>

        <div>
          <Eyebrow>Élargir à</Eyebrow>
          <div className="grid sm:grid-cols-2 gap-2 mt-2">
            {ELARGIR.map(([cle, texte]) => (
              <label key={cle} className="flex items-center gap-2 text-sm cursor-pointer">
                <Case cochee={!!options[cle]} testid={`export-${cle}`}
                  onChange={(v) => setOptions({ ...options, [cle]: v })} />
                {texte}
              </label>
            ))}
          </div>
        </div>

        <div>
          <Eyebrow>Filtrer</Eyebrow>
          <div className="grid sm:grid-cols-3 gap-3 mt-2">
            <ChampChoix label="Semestre" valeur={filtres.semestre} testid="export-semestre"
              options={[{ valeur: "", label: "les deux" }, { valeur: "1", label: "semestre 1" }, { valeur: "2", label: "semestre 2" }]}
              onChange={(v) => setFiltres({ ...filtres, semestre: v })} />
            <ChampTexte label="Cours" valeur={filtres.cours} testid="export-cours"
              onChange={(v) => setFiltres({ ...filtres, cours: v })} />
            <ChampChoix label="Jour" valeur={filtres.jour} testid="export-jour"
              options={[{ valeur: "", label: "tous" }, "lundi", "mardi", "mercredi", "jeudi", "vendredi", "samedi"]}
              onChange={(v) => setFiltres({ ...filtres, jour: v })} />
            <ChampTexte label="Discipline" valeur={filtres.discipline} testid="export-discipline"
              onChange={(v) => setFiltres({ ...filtres, discipline: v })} />
            <ChampTexte label="Professeur" valeur={filtres.professeur} testid="export-professeur"
              onChange={(v) => setFiltres({ ...filtres, professeur: v })} />
            {/* Les cadeaux de parrainage et les dossiers J+S se préparent par tranche d'âge. */}
            <ChampChoix label="Âge" valeur={filtres.age_max} testid="export-age"
              options={[{ valeur: "", label: "Tous les âges" },
                        { valeur: "12", label: "Jusqu'à 12 ans" },
                        { valeur: "20", label: "Jusqu'à 20 ans" }]}
              onChange={(v) => setFiltres({ ...filtres, age_max: v })} />
          </div>
        </div>

        <div>
          <Eyebrow>Colonnes</Eyebrow>
          <div className="grid sm:grid-cols-2 gap-2 mt-2">
            {JEUX.map(([cle, titre, detail]) => (
              <button key={cle} data-testid={`export-jeu-${cle}`} onClick={() => setJeu(cle)}
                className="text-left rounded-2xl px-4 py-3 border transition-colors"
                style={{
                  borderColor: jeu === cle ? "var(--primary)" : "var(--border)",
                  background: jeu === cle ? "var(--primary-tint)" : "#fff",
                }}>
                <div className="text-sm font-medium">{titre}</div>
                <div className="text-xs text-[var(--muted)]">{detail}</div>
              </button>
            ))}
          </div>
        </div>

        <div className="flex flex-wrap gap-3 pt-2">
          <BoutonSombre onClick={() => sortir("xlsx")} testid="export-xlsx"
            disabled={!apercu || !apercu.nb}>
            <FileSpreadsheet size={14} /> Télécharger en Excel
          </BoutonSombre>
          <button className="btn-ghost" data-testid="export-csv" onClick={() => sortir("csv")}
            disabled={!apercu || !apercu.nb}>
            <FileText size={14} /> Télécharger en CSV
          </button>
        </div>
      </div>
    </Modale>
  );
}

export const BoutonExport = ({ onClick }) => (
  <button className="btn-ghost" data-testid="eleves-exporter" onClick={onClick}>
    <Download size={14} /> Exporter
  </button>
);
